/**
 * Zod schema for the Create Request opportunity project fields (RHF). Validates the **request** payload.
 *
 * **Figma (Visual Template)** – field-level error color, help text, validation patterns:
 * `https://www.figma.com/design/nzRrn94pPJjuXdhfsuRMN6/Visual-Template?node-id=7558-17274` (m=dev)
 * Inline messages are full sentences; `formValidation.formatValidationErrors` does not double-append "is required" for those.
 */

import { z } from 'zod';

import { multilineRichHtmlMaxLength } from '../utils/richContent/multilineRichHtmlFields';
import { validateTextAreaField } from '../utils/richContent/sanitizeTextArea';

import type {
  FormValidationError,
  FormValidationResult,
} from '../utils/formValidation';

const VALIDATION = {
  PHONE_MAX_LENGTH: 50,
  HTML_PATTERN: /<[^>]*>/,
} as const;

export const OPPORTUNITY_PROJECT_DESCRIPTION_MAX = 100000;
export const OPPORTUNITY_ADDITIONAL_INFORMATION_MAX = 10_000;

function richHtmlFieldSchema(maxPlainTextLength: number, fieldLabel: string) {
  return z.string().superRefine((val, ctx) => {
    if (val === '') {
      return;
    }
    // In-progress form HTML may still use pending image refs until the first
    // save flushes them to sitescope-attachment:// ids. Transport stays strict.
    const result = validateTextAreaField(val, {
      maxPlainTextLength,
      fieldLabel,
      allowPendingAttachmentRefs: true,
    });
    if (!result.ok) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: result.message,
      });
    }
  });
}

/**
 * Project type (single select). Per #954 / FR2 — **not** scope; prefab/modular belongs under
 * {@link OPPORTUNITY_PROJECT_SCOPE_OPTIONS} only (`PreFab / Modular`).
 */
export const OPPORTUNITY_PROJECT_TYPE_OPTIONS = [
  'Closet/Lab',
  'Data Center 10+ Racks',
  'Other',
] as const;

/** Dropdown sentinel. The saved value is the typed text, not this label. */
export const OTHER_SPECIFY_OPTION = 'Other (specify)';

export const OPPORTUNITY_VERTICAL_OPTIONS = [
  'Healthcare',
  'Finance',
  'Technology',
  'Government',
  'Colo',
  'NeoCloud',
  'Hyperscale',
  OTHER_SPECIFY_OPTION,
] as const;

export const OPPORTUNITY_USECASE_OPTIONS = [
  'Proof of Concept',
  'Employee Safety',
  'RAG',
  OTHER_SPECIFY_OPTION,
] as const;

/** Listed option, including an empty selection. Custom text is not listed. */
export function dropdownValueForStored(
  stored: string,
  options: readonly string[],
): string {
  const trimmed = stored.trim();
  if (trimmed === '' || options.includes(trimmed)) {
    return trimmed;
  }
  return OTHER_SPECIFY_OPTION;
}

/** Typed text when the stored value is not one of the listed options. */
export function specifyTextForStored(
  stored: string,
  options: readonly string[],
): string {
  const trimmed = stored.trim();
  if (
    trimmed === '' ||
    trimmed === OTHER_SPECIFY_OPTION ||
    options.includes(trimmed)
  ) {
    return '';
  }
  return trimmed;
}

/**
 * Message when a Vertical or Use case value cannot be saved.
 * Absent and blank values are allowed. The dropdown sentinel is not.
 */
export function opportunitySpecifySaveError(
  values: Record<string, unknown>,
): string | null {
  const fields = [
    { key: 'oppVertical', label: 'Vertical' },
    { key: 'oppUsecase', label: 'Use case' },
  ] as const;
  for (const { key, label } of fields) {
    if (!(key in values)) {
      continue;
    }
    const value = values[key];
    if (value == null || value === '') {
      continue;
    }
    if (typeof value !== 'string') {
      return `${label} must be text.`;
    }
    const trimmed = value.trim();
    if (trimmed === '') {
      continue;
    }
    if (trimmed === OTHER_SPECIFY_OPTION) {
      return `Specify a value for ${label}.`;
    }
    if (trimmed.length > 255) {
      return `${label} must be 255 characters or less.`;
    }
    if (VALIDATION.HTML_PATTERN.test(trimmed)) {
      return `${label} cannot contain HTML.`;
    }
  }
  return null;
}

export type OpportunityProjectTypeLabel =
  (typeof OPPORTUNITY_PROJECT_TYPE_OPTIONS)[number];

const PROJECT_TYPE_SET = new Set<string>(OPPORTUNITY_PROJECT_TYPE_OPTIONS);

function optionOrRequiredSpecify(specifyMessage: string) {
  return z
    .string()
    .refine(val => val.trim().length <= 255, {
      message: 'Must be 255 characters or less.',
    })
    .refine(
      val => val.trim() === '' || !VALIDATION.HTML_PATTERN.test(val.trim()),
      {
        message: 'HTML tags are not allowed',
      },
    )
    .refine(val => val.trim() !== OTHER_SPECIFY_OPTION, {
      message: specifyMessage,
    });
}

/** Header counts treat an open Specify box with no words as empty. */
export function blankOpportunitySpecifySentinel(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...values };
  for (const key of ['oppVertical', 'oppUsecase'] as const) {
    if (
      typeof next[key] === 'string' &&
      next[key].trim() === OTHER_SPECIFY_OPTION
    ) {
      next[key] = '';
    }
  }
  return next;
}

/**
 * Multi-select workload picks. Keep in sync with `server/constants/opportunity.ts`
 * `OPP_WORKLOAD_TYPE_OPTIONS` (the RN/Node build boundary forces the duplication).
 *
 * Migration 092 widened `opp_workload_type` to NVARCHAR(MAX) and the mapper
 * serializes as a JSON array (same shape as `oppProjectScope`). `'AI'` is a real
 * option, so a legacy `'AI'` DB value maps to it (rather than dropping); `'Both'`
 * (retired 2026-07-13) and the old NVL labels drop and render as unselected.
 */
export const OPPORTUNITY_WORKLOAD_TYPE_OPTIONS = [
  'Vertiv™ SmartIT - Enterprise AI for Blackwell NVL72 GB300',
  'Vertiv™ SmartIT - Enterprise AI for Rubin NVL8',
  'AI',
  'Traditional Compute',
] as const;

export type OpportunityWorkloadTypeLabel =
  (typeof OPPORTUNITY_WORKLOAD_TYPE_OPTIONS)[number];

const WORKLOAD_TYPE_SET = new Set<string>(OPPORTUNITY_WORKLOAD_TYPE_OPTIONS);

/** Workload option that gates `smartItNvl72Config`. */
export const WORKLOAD_TYPE_AI_NVL72: OpportunityWorkloadTypeLabel =
  'Vertiv™ SmartIT - Enterprise AI for Blackwell NVL72 GB300';
/** Workload option that gates `smartItNvl8Config`. */
export const WORKLOAD_TYPE_AI_NVL8: OpportunityWorkloadTypeLabel =
  'Vertiv™ SmartIT - Enterprise AI for Rubin NVL8';

/**
 * Display-label override per workload option VALUE (dropdown chips + option rows).
 *
 * Currently an IDENTITY map: the stored values are already the user-facing text, so
 * `formatWorkloadTypeLabel` returns its input for every option. Kept as the seam for
 * the case where a stored value must diverge from its label again (it did
 * previously, when the values carried an `AI - ` prefix and a ` - 3 Racks` suffix).
 * Mirror of `server/constants/opportunity.ts` `WORKLOAD_TYPE_DISPLAY_LABELS` — keep
 * the two in sync.
 */
export const WORKLOAD_TYPE_DISPLAY_LABELS: Record<string, string> = {
  [WORKLOAD_TYPE_AI_NVL72]:
    'Vertiv™ SmartIT - Enterprise AI for Blackwell NVL72 GB300',
  [WORKLOAD_TYPE_AI_NVL8]: 'Vertiv™ SmartIT - Enterprise AI for Rubin NVL8',
};

/** Friendly display for a single stored workload value (identity if unmapped). */
export function formatWorkloadTypeLabel(value: string): string {
  return WORKLOAD_TYPE_DISPLAY_LABELS[value] ?? value;
}

/** Single-select rack configuration options gated by the NVL72 workload option. */
export const OPPORTUNITY_SMART_IT_NVL72_CONFIG_OPTIONS = [
  '1 x storage',
  '1 x support',
  '1 x support + 1 x storage',
] as const;

export type OpportunitySmartItNvl72ConfigLabel =
  (typeof OPPORTUNITY_SMART_IT_NVL72_CONFIG_OPTIONS)[number];

const SMART_IT_NVL72_CONFIG_SET = new Set<string>(
  OPPORTUNITY_SMART_IT_NVL72_CONFIG_OPTIONS,
);

/** Single-select rack configuration options gated by the NVL8 workload option. */
export const OPPORTUNITY_SMART_IT_NVL8_CONFIG_OPTIONS = [
  '1 node',
  '2 nodes',
  '4 nodes',
  '6 nodes',
  '8 nodes',
  '9 nodes',
] as const;

export type OpportunitySmartItNvl8ConfigLabel =
  (typeof OPPORTUNITY_SMART_IT_NVL8_CONFIG_OPTIONS)[number];

const SMART_IT_NVL8_CONFIG_SET = new Set<string>(
  OPPORTUNITY_SMART_IT_NVL8_CONFIG_OPTIONS,
);

/**
 * Each Smart IT config form field and the workload option that gates it. Client
 * twin of `server/constants/opportunity.ts` `SMART_IT_CONFIG_GATES`. Data-driven so
 * the clear-on-workload-change rule cannot drift from the server orphan guard.
 */
export const SMART_IT_CONFIG_GATES = [
  { field: 'smartItNvl72Config', gate: WORKLOAD_TYPE_AI_NVL72 },
  { field: 'smartItNvl8Config', gate: WORKLOAD_TYPE_AI_NVL8 },
] as const;

export type SmartItConfigField =
  (typeof SMART_IT_CONFIG_GATES)[number]['field'];

/**
 * Given the currently-selected workload picks, returns the Smart IT config fields
 * whose gate is NOT selected — i.e. the fields the form must clear. Server twin:
 * `orphanedSmartItConfigColumns` in `server/utils/smartItWorkload.ts`.
 */
export function smartItConfigFieldsToClearOnWorkloadChange(
  selected: readonly string[],
): SmartItConfigField[] {
  const picks = new Set(selected);
  return SMART_IT_CONFIG_GATES.filter(g => !picks.has(g.gate)).map(
    g => g.field,
  );
}

/** Aligned with Opportunity step UI; stored as a JSON string array in form payload. Keep in sync with `server/constants/opportunity.ts` `OPP_PROJECT_SCOPE_OPTIONS`. */
export const OPPORTUNITY_PROJECT_SCOPE_OPTIONS = [
  'Facility Power',
  'Rack',
  'Small UPS',
  'Cooling',
  'Liquid Cooling',
  'PreFab / Modular',
  'Other',
] as const;

export type OpportunityProjectScopeLabel =
  (typeof OPPORTUNITY_PROJECT_SCOPE_OPTIONS)[number];

function toStr(v: unknown): string {
  if (v == null) {
    return '';
  }
  return String(v);
}

function toTrimmedStr(v: unknown): string {
  return toStr(v).trim();
}

const SCOPE_SET = new Set<string>(OPPORTUNITY_PROJECT_SCOPE_OPTIONS);

/** Older form labels → current scope option (best-effort migration on read). */
const LEGACY_PROJECT_SCOPE_ALIASES: Record<
  string,
  OpportunityProjectScopeLabel
> = {
  Power: 'Facility Power',
  Racks: 'Rack',
};

function normalizeScopeLabel(raw: string): OpportunityProjectScopeLabel | null {
  const t = raw.trim();
  if (t === '' || t === 'Full assessment') {
    return null;
  }
  const mapped = LEGACY_PROJECT_SCOPE_ALIASES[t] ?? t;
  return SCOPE_SET.has(mapped)
    ? (mapped as OpportunityProjectScopeLabel)
    : null;
}

function compactScopeArray(tokens: string[]): OpportunityProjectScopeLabel[] {
  const out: OpportunityProjectScopeLabel[] = [];
  const seen = new Set<string>();
  for (const raw of tokens) {
    const n = normalizeScopeLabel(raw);
    if (n != null && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out;
}

/**
 * Coerces `oppWorkloadType` payload variants (string[], JSON-array string, CSV, or a
 * single value) into a clean array of allowed workload labels. A value survives only
 * if it is a member of the current `OPPORTUNITY_WORKLOAD_TYPE_OPTIONS`. Since `'AI'`
 * is a real option, a legacy `'AI'` value maps to it; `'Both'` (retired 2026-07-13)
 * and the superseded NVL labels drop and render as unselected (no migration).
 *
 * Server twin: `parseWorkloadSelection` in `server/utils/smartItWorkload.ts`.
 */
export function toWorkloadTypeArrayFromForm(v: unknown): string[] {
  const compact = (tokens: string[]): string[] => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of tokens) {
      const trimmed = raw.trim();
      if (
        trimmed === '' ||
        !WORKLOAD_TYPE_SET.has(trimmed) ||
        seen.has(trimmed)
      ) {
        continue;
      }
      seen.add(trimmed);
      out.push(trimmed);
    }
    return out;
  };
  if (Array.isArray(v)) {
    return compact(
      (v as unknown[]).filter((x): x is string => typeof x === 'string'),
    );
  }
  if (v == null || v === '') {
    return [];
  }
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '') {
      return [];
    }
    if (t.startsWith('[')) {
      try {
        const parsed: unknown = JSON.parse(t);
        if (Array.isArray(parsed)) {
          return compact(
            (parsed as unknown[]).filter(
              (x): x is string => typeof x === 'string',
            ),
          );
        }
      } catch {
        /* treat as plain string */
      }
    }
    if (t.includes(',')) {
      return compact(t.split(','));
    }
    return compact([t]);
  }
  return [];
}

/**
 * Coerces legacy `oppProjectScope` (string, CSV, or JSON) into a clean string array
 * of allowed scope labels for this step.
 */
export function toProjectScopeArrayFromForm(v: unknown): string[] {
  if (Array.isArray(v)) {
    return compactScopeArray(
      (v as unknown[]).filter((x): x is string => typeof x === 'string'),
    );
  }
  if (v == null || v === '') {
    return [];
  }
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '') {
      return [];
    }
    if (t.startsWith('[')) {
      try {
        const parsed: unknown = JSON.parse(t);
        if (Array.isArray(parsed)) {
          return compactScopeArray(
            (parsed as unknown[]).filter(
              (x): x is string => typeof x === 'string',
            ),
          );
        }
      } catch {
        /* treat as plain string */
      }
    }
    if (t.includes(',')) {
      return compactScopeArray(t.split(',').map(s => s.trim()));
    }
    const one = normalizeScopeLabel(t);
    return one != null ? [one] : [];
  }
  return [];
}

/**
 * Legacy column `opp_is_integrated_solution`: set when **PreFab / Modular** is selected in
 * project scope (#954). No separate Yes/No in the simplified Project Details flow.
 */
export function deriveOppIsIntegratedSolutionFromScope(scope: unknown): string {
  return toProjectScopeArrayFromForm(scope).includes('PreFab / Modular')
    ? 'Yes'
    : '';
}

/** Prefer `oppApproximateKw`; fall back to min/max for legacy payloads. */
function coalesceOppApproximateKw(formData: Record<string, unknown>): string {
  const direct = toStr(formData.oppApproximateKw);
  if (direct) {
    return direct;
  }
  const min = toStr(formData.oppApproximateKwMin);
  const max = toStr(formData.oppApproximateKwMax);
  if (min && max) {
    return `${min} to ${max} kW`;
  }
  if (min) {
    return min;
  }
  if (max) {
    return max;
  }
  return '';
}

/**
 * Requestor is collected outside this Opportunity-details step (e.g. profile/defaults in `formData`)
 * but is still validated on save.
 */
function createRequestRequestorZod() {
  return z.object({
    requestorName: z
      .string()
      .max(255, { message: 'Name must be 255 characters or less' })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
    requestorRole: z
      .string()
      .max(255, { message: 'Role must be 255 characters or less' })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
    requestorEmail: z
      .string()
      .min(1, { message: 'Enter a work email address.' })
      .email({ message: 'Enter a valid email address.' }),
    requestorPhone: z.string().max(VALIDATION.PHONE_MAX_LENGTH, {
      message: `Phone must be ${String(
        VALIDATION.PHONE_MAX_LENGTH,
      )} characters or less`,
    }),
  });
}

/**
 * RHF in `ProjectInformation` for request fields only (no requestor on this step).
 */
function opportunityRequestDetailsZod() {
  return z.object({
    customerName: z.string().min(1, { message: 'Enter a customer name.' }),
    partnerName: z
      .string()
      .max(255, { message: 'Channel Partner must be 255 characters or less' })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
    oppVertical: optionOrRequiredSpecify('Specify the vertical.'),
    oppUsecase: optionOrRequiredSpecify('Specify the use case.'),
    oppProjectName: z
      .string()
      .max(255, { message: 'Project name must be 255 characters or less.' })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
    region: z.string().min(1, { message: 'Select a region.' }),
    oppProjectType: z
      .string()
      .refine(v => v === '' || PROJECT_TYPE_SET.has(v), {
        message: 'Select a listed project type.',
      }),
    oppProjectScope: z
      .array(z.string())
      .refine(a => a.every(s => SCOPE_SET.has(s)), {
        message: 'Select only listed project scope options.',
      })
      .refine(a => a.length === new Set(a).size, {
        message: 'Do not add the same scope twice.',
      }),
    oppDeploymentTimeline: z
      .string()
      .max(200, { message: 'Timeline must be 200 characters or less' })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
    oppBudget: z
      .string()
      .max(200, { message: 'Budget must be 200 characters or less' })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
    oppQuoteNumber: z
      .string()
      .max(255, { message: 'Quote number must be 255 characters or less' })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
    oppApproximateKw: z
      .string()
      .max(200, { message: 'Approximate kW must be 200 characters or less' })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
    oppProjectDescription: richHtmlFieldSchema(
      OPPORTUNITY_PROJECT_DESCRIPTION_MAX,
      'Description',
    ),
    /** Opportunity Information “Additional Information” (RBAC field id `siteAdditionalSiteInformation`). */
    siteAdditionalSiteInformation: richHtmlFieldSchema(
      OPPORTUNITY_ADDITIONAL_INFORMATION_MAX,
      'Additional Information',
    ),
    /** Captured on Site Walk / detail; optional on Project Details step. */
    oppSiteAddress: richHtmlFieldSchema(
      multilineRichHtmlMaxLength('oppSiteAddress'),
      'Site address',
    ),
    dealRegistrationNumber: z
      .string()
      .max(200, {
        message: 'Deal registration number must be 200 characters or less.',
      })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
    oppOpportunityId: z
      .string()
      .max(200, {
        message: 'Opportunity number must be 200 characters or less.',
      })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
    // Multi-select since migration 092 (Smart IT) — same array shape as
    // oppProjectScope.
    oppWorkloadType: z
      .array(z.string())
      .refine(a => a.every(s => WORKLOAD_TYPE_SET.has(s)), {
        message: 'Select only listed workload type options.',
      })
      .refine(a => a.length === new Set(a).size, {
        message: 'Do not add the same workload type twice.',
      }),
    smartItNvl72Config: z
      .string()
      .refine(v => v === '' || SMART_IT_NVL72_CONFIG_SET.has(v), {
        message:
          'Select a listed configuration for Vertiv™ SmartIT - Enterprise AI for Blackwell NVL72 GB300.',
      }),
    smartItNvl8Config: z
      .string()
      .refine(v => v === '' || SMART_IT_NVL8_CONFIG_SET.has(v), {
        message:
          'Select a listed configuration for Vertiv™ SmartIT - Enterprise AI for Rubin NVL8.',
      }),
    oppTeamNotes: z
      .string()
      .max(10000, { message: 'Team notes must be 10,000 characters or less.' })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
    oppSiteAssessmentRequired: z
      .string()
      .refine(v => v === '' || v === 'Yes' || v === 'No' || v === 'Not Sure', {
        message: 'Select Yes, No, or Not Sure for site assessment required.',
      }),
    oppPreferredAssessmentDate: z
      .string()
      .max(200, {
        message: 'Preferred assessment date must be 200 characters or less.',
      })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
    oppSiteWalkNotes: z
      .string()
      .max(10000, {
        message: 'Site Walk notes must be 10,000 characters or less.',
      })
      .refine(val => val === '' || !VALIDATION.HTML_PATTERN.test(val), {
        message: 'HTML tags are not allowed',
      }),
  });
}

export function oppDetailsRhfZodSchema() {
  return opportunityRequestDetailsZod().omit({
    oppTeamNotes: true,
    oppSiteAssessmentRequired: true,
    oppPreferredAssessmentDate: true,
    oppSiteAddress: true,
    oppSiteWalkNotes: true,
  });
}

function oppDetailsFullZodSchema() {
  return createRequestRequestorZod().merge(opportunityRequestDetailsZod());
}

export type OpportunityRequestDetailsRhfValues = z.infer<
  ReturnType<typeof oppDetailsRhfZodSchema>
>;

export type OppDetailsZodValues = z.infer<
  ReturnType<typeof oppDetailsFullZodSchema>
>;

/** Full payload (includes requestor) for save / Zod on submit. */
export function buildOppDetailsZodInput(
  formData: Record<string, unknown>,
): OppDetailsZodValues {
  return {
    requestorName: toStr(formData.requestorName),
    requestorRole: toStr(formData.requestorRole),
    requestorEmail: toStr(formData.requestorEmail),
    requestorPhone: toStr(formData.requestorPhone),
    customerName: toStr(formData.customerName),
    partnerName: toStr(formData.partnerName),
    oppVertical: toStr(formData.oppVertical),
    oppUsecase: toStr(formData.oppUsecase),
    oppProjectName: toStr(formData.oppProjectName),
    region: toStr(formData.region),
    oppProjectType: toStr(formData.oppProjectType),
    oppProjectScope: toProjectScopeArrayFromForm(formData.oppProjectScope),
    oppDeploymentTimeline: toStr(formData.oppDeploymentTimeline),
    oppBudget: toStr(formData.oppBudget),
    oppQuoteNumber: toTrimmedStr(formData.oppQuoteNumber),
    oppApproximateKw: coalesceOppApproximateKw(formData),
    oppProjectDescription: toStr(formData.oppProjectDescription),
    siteAdditionalSiteInformation: toStr(
      formData.siteAdditionalSiteInformation,
    ),
    // Truncating would cut sanitized HTML mid-tag; the schema rejects overlong values instead.
    oppSiteAddress: toStr(formData.oppSiteAddress),
    dealRegistrationNumber: toStr(formData.dealRegistrationNumber),
    oppOpportunityId: toStr(formData.oppOpportunityId),
    oppWorkloadType: toWorkloadTypeArrayFromForm(formData.oppWorkloadType),
    smartItNvl72Config: toStr(formData.smartItNvl72Config),
    smartItNvl8Config: toStr(formData.smartItNvl8Config),
    oppIsIntegratedSolution: deriveOppIsIntegratedSolutionFromScope(
      formData.oppProjectScope,
    ),
    oppTeamNotes: toStr(formData.oppTeamNotes),
    oppSiteAssessmentRequired: toStr(formData.oppSiteAssessmentRequired),
    oppPreferredAssessmentDate: toStr(formData.oppPreferredAssessmentDate),
    oppSiteWalkNotes: toStr(formData.oppSiteWalkNotes),
  };
}

/** Values for React Hook Form (same step fields as `oppDetailsRhfZodSchema`). */
export function buildOpportunityRequestDetailsRhfValues(
  formData: Record<string, unknown>,
): OpportunityRequestDetailsRhfValues {
  const full = buildOppDetailsZodInput(formData);
  const {
    requestorName,
    requestorRole,
    requestorEmail,
    requestorPhone,
    oppTeamNotes,
    oppSiteAssessmentRequired,
    oppPreferredAssessmentDate,
    oppIsIntegratedSolution,
    oppSiteAddress,
    oppSiteWalkNotes,
    ...rhf
  } = full;
  // Stripped; values live on parent `formData` and full save Zod, not in RHF.
  // eslint-disable-next-line no-void -- intentional no-op to mark tuple as used for destructuring-only
  void (requestorName,
  requestorRole,
  requestorEmail,
  requestorPhone,
  oppTeamNotes,
  oppSiteAssessmentRequired,
  oppPreferredAssessmentDate,
  oppIsIntegratedSolution,
  oppSiteAddress,
  oppSiteWalkNotes);
  return rhf;
}

export function zodIssuesToFormValidationErrors(
  error: z.ZodError,
): FormValidationError[] {
  return error.issues.map(issue => ({
    field: (issue.path[0] as string) ?? 'unknown',
    message: issue.message,
  }));
}

export function validateOppDetails(
  formData: Record<string, unknown>,
): FormValidationResult {
  const schema = oppDetailsFullZodSchema();
  const result = schema.safeParse(buildOppDetailsZodInput(formData));
  const zodErrs: FormValidationError[] = result.success
    ? []
    : zodIssuesToFormValidationErrors(result.error);

  return { isValid: zodErrs.length === 0, errors: zodErrs };
}
